/**
 * @function AssignTestButton
 * @description Component for assigning a test to a participant.
 * @returns {JSX.Element | null} - The AssignTestButton component.
 */

import { useEffect } from 'react'
import PlusIcon from '../assets/plus.svg'
import { useModal } from '../hooks/useModal'
import { useGetBatteriesByStudyIdQuery } from '../services/batteries'
import { useCreateAssignmentInviteMutation } from '../services/invites'
import { useGetParticipantByIdQuery } from '../services/participants'
import { ConfirmNewTest } from './modals/content/ConfirmNewTest'
import { ShareAssignment } from './modals/content/ShareAssignment'

/**
 * @interface AssignTestButtonProps
 * @description Props for the AssignTestButton component.
 * @property {string} participantId - The ID of the participant.
 * @property {string} studyId - The ID of the study.
 */
interface AssignTestButtonProps {
    participantId: string
    studyId: string
}

/**
 * @function AssignTestButton
 * @description Component for assigning a test to a participant.
 * @returns {JSX.Element | null} - The AssignTestButton component.
 */
export function AssignTestButton({ participantId, studyId }: AssignTestButtonProps) {
    const [open] = useModal()
    const { data: batteries, isLoading: areBatteriesLoading } =
        useGetBatteriesByStudyIdQuery(studyId)
    const [
        createAssignmentInvite,
        { isLoading: isProcessing, isSuccess: isAssignmentCreated, data },
    ] = useCreateAssignmentInviteMutation()
    const {
        data: user,
        isLoading: isUserLoading,
        isSuccess: hasUser,
    } = useGetParticipantByIdQuery(participantId)

    useEffect(() => {
        if (isAssignmentCreated && hasUser) {
            open({
                content: (
                    <ShareAssignment
                        isPhoneVerified={user.is_phone_verified && user.is_sms_permitted}
                        isEmailVerified={user.is_email_defined}
                        invitationId={data?.id}
                        participantId={participantId}
                    />
                ),
            })
        }
    }, [isAssignmentCreated, hasUser])

    async function createNewTest() {
        createAssignmentInvite({
            battery_id: batteries?.[0]?.id,
            patient_id: participantId,
            study_id: user?.studies[0].study.id,
            check_incomplete_tests: true,
        })
            .unwrap()
            .catch(() => {
                open({
                    content: (
                        <ConfirmNewTest
                            onConfirm={async () => {
                                createAssignmentInvite({
                                    battery_id: batteries?.[0]?.id,
                                    patient_id: participantId,
                                    max_assignments: 1,
                                    study_id: user?.studies[0].study.id,
                                    check_incomplete_tests: false,
                                    cancel_incomplete_tests: true,
                                })
                            }}
                        />
                    ),
                })
            })
    }

    return (
        <button
            disabled={areBatteriesLoading || isUserLoading || isProcessing}
            className="px-4 py-2 rounded-md flex items-center text-sm font-medium bg-indigo-600 text-white"
            onClick={createNewTest}
        >
            <PlusIcon className="mr-2 fill-white" />
            Assign Test
        </button>
    )
}
